import apiClient from './client'
import type {
  LoginRequest, LoginResponse,
  DataSource, DataSourcePagedResponse, DataSourceSearchParams,
  GetDataSourceParameterOptionsBody, GetDataSourceParameterOptionsResponse,
  Template, TemplatePagedResponse, TemplateSearchParams,
  TemplateExportPack, TemplateImportPackResult,
  CloneTemplateRequest, CloneTemplateResult,
  CloneDataSourceRequest, CloneDataSourceResult,
  TemplateActivationState,
  TemplateTagsParams, GetTemplateTagsResponse,
  WebhookSubscription, WebhookSubscriptionPagedResponse, WebhookSubscriptionSearchParams,
  WebhookDeliveryAttemptPagedResponse, WebhookDeliveryAttemptSearchParams, WebhookDeliveryRetryBatchResult,
  Snippet, SnippetPagedResponse, SnippetSearchParams,
  TableSchema, ColumnSchema, PreviewResult,
  RenderRequest, PdfExportRequest, ExcelExportRequest, DesignerPreviewRequest,
} from '@/types'

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const authApi = {
  login: (data: LoginRequest) =>
    //apiClient.post<LoginResponse>('/api/Account/login', data).then(r => r.data),
    apiClient.post<LoginResponse>('/api/v1/auth/login', data).then(r => r.data),
}

// ─── Schema ───────────────────────────────────────────────────────────────────

export const schemaApi = {
  getTables: () =>
    apiClient.get<TableSchema[]>('/api/v1/schema/tables').then(r => r.data),

  getColumns: (tableName: string) =>
    apiClient.get<ColumnSchema[]>(`/api/v1/schema/tables/${tableName}/columns`).then(r => r.data),

  preview: (sql: string) =>
    apiClient.post<PreviewResult>('/api/v1/schema/preview', { sql }).then(r => r.data),
}

// ─── Data Sources ─────────────────────────────────────────────────────────────

export const dataSourceApi = {
  getAll: (params: DataSourceSearchParams = {}) => {
    const query = new URLSearchParams()
    if (params.filter)   query.set('filter', params.filter)
    if (params.pageNumber)     query.set('pageNumber', String(params.pageNumber))
    if (params.pageSize) query.set('pageSize', String(params.pageSize))
    const qs = query.toString()
    return apiClient
      .get<DataSourcePagedResponse>(`/api/v1/datasources${qs ? `?${qs}` : ''}`)
      .then(r => r.data)
  },

  getById: (id: string) =>
    apiClient.get<DataSource>(`/api/v1/datasources/${id}`).then(r => r.data),

  create: (data: Omit<DataSource, 'id'>) =>
    apiClient.post<DataSource>('/api/v1/datasources', data).then(r => r.data),

  update: (id: string, data: Omit<DataSource, 'id'>) =>
    apiClient.put(`/api/v1/datasources/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/api/v1/datasources/${id}`),

  getParameterOptions: (id: string, body: GetDataSourceParameterOptionsBody) =>
    apiClient
      .post<GetDataSourceParameterOptionsResponse>(`/api/v1/datasources/${id}/parameter-options`, body)
      .then(r => r.data),

  clone: (id: string, data: CloneDataSourceRequest) =>
    apiClient.post<CloneDataSourceResult>(`/api/v1/datasources/${id}/clone`, data).then(r => r.data),
}

// ─── Templates ────────────────────────────────────────────────────────────────

export const templateApi = {
  getAll: (params: TemplateSearchParams = {}) => {
    const query = new URLSearchParams()
    if (params.filter)   query.set('filter', params.filter)
    if (params.includeDeleted !== undefined) query.set('includeDeleted', String(params.includeDeleted))
    if (params.pageNumber)     query.set('pageNumber', String(params.pageNumber))
    if (params.pageSize) query.set('pageSize', String(params.pageSize))
    params.tags?.forEach(t => query.append('Tags', t))
    const qs = query.toString()
    return apiClient
      .get<TemplatePagedResponse>(`/api/v1/templates${qs ? `?${qs}` : ''}`)
      .then(r => r.data)
  },

  getById: (id: string) =>
    apiClient.get<Template>(`/api/v1/templates/${id}`).then(r => r.data),

  /**
   * Distinct tags across all templates, or only those linked to the given ids/codes.
   * Query keys must match API: repeated `TemplateIds` / `TemplateCodes`.
   */
  getTags: (params?: TemplateTagsParams) => {
    const q = new URLSearchParams()
    params?.templateIds?.forEach(id => q.append('TemplateIds', id))
    params?.templateCodes?.forEach(c => q.append('TemplateCodes', c))
    if (params?.includeDeleted !== undefined) q.set('includeDeleted', String(params.includeDeleted))
    const qs = q.toString()
    return apiClient
      .get<GetTemplateTagsResponse>(`/api/v1/templates/tags${qs ? `?${qs}` : ''}`)
      .then(r => r.data)
  },

  create: (data: Omit<Template, 'id'>) =>
    apiClient.post<Template>('/api/v1/templates', data).then(r => r.data),

  update: (id: string, data: Omit<Template, 'id'>) =>
    apiClient.put(`/api/v1/templates/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/api/v1/templates/${id}`),

  restore: (id: string) =>
    apiClient.post(`/api/v1/templates/${id}/restore`),

  publish: (templateIds: string[], webhookSubscriptionIds?: string[]) =>
    apiClient.post('/api/v1/templates/publish', { templateIds, webhookSubscriptionIds }).then(r => r.data),

  exportPack: (templateIds: string[]) =>
    apiClient.post<TemplateExportPack>('/api/v1/templates/export-pack', { templateIds }).then(r => r.data),

  importPack: (pack: TemplateExportPack, conflictResolution: 'upsert' | 'fail' = 'upsert') =>
    apiClient
      .post<TemplateImportPackResult>('/api/v1/templates/import-pack', { ...pack, conflictResolution })
      .then(r => r.data),

  clone: (id: string, data: CloneTemplateRequest) =>
    apiClient.post<CloneTemplateResult>(`/api/v1/templates/${id}/clone`, data).then(r => r.data),

  toggleActive: (templateStates: TemplateActivationState[], webhookSubscriptionIds?: string[]) =>
    apiClient.post('/api/v1/templates/active', { template: templateStates, webhookSubscriptionIds }).then(r => r.data),

  renderHtml: (id: string, request: RenderRequest = {}) =>
    apiClient.post(`/api/v1/templates/${id}/render`, request, {
      responseType: 'text',
    }).then(r => r.data as string),

  exportPdf: (id: string, request: PdfExportRequest = {}) =>
    apiClient.post(`/api/v1/templates/${id}/pdf`, request, {
      responseType: 'blob',
    }).then(r => r.data as Blob),

  exportExcel: (id: string, request: ExcelExportRequest = {}) =>
    apiClient.post(`/api/v1/templates/${id}/excel`, request, {
      responseType: 'blob',
    }).then(r => r.data as Blob),

  designerPreview: (request: DesignerPreviewRequest) =>
    apiClient.post('/api/v1/templates/preview', request, {
      responseType: 'text',
    }).then(r => r.data as string),
}

// ─── Snippets ─────────────────────────────────────────────────────────────────

export const snippetApi = {
  getAll: (params: SnippetSearchParams = {}) => {
    const query = new URLSearchParams()
    if (params.filter) query.set('filter', params.filter)
    if (params.type) query.set('type', params.type)
    if (params.pageNumber) query.set('pageNumber', String(params.pageNumber))
    if (params.pageSize) query.set('pageSize', String(params.pageSize))
    const qs = query.toString()
    return apiClient
      .get<SnippetPagedResponse>(`/api/v1/snippets${qs ? `?${qs}` : ''}`)
      .then(r => r.data)
  },

  getById: (id: string) =>
    apiClient.get<Snippet>(`/api/v1/snippets/${id}`).then(r => r.data),

  create: (data: Omit<Snippet, 'id' | 'createdAt' | 'updatedAt' | 'version'>) =>
    apiClient.post<{ id: string; name: string; success: boolean }>('/api/v1/snippets', data).then(r => r.data),

  update: (id: string, data: Omit<Snippet, 'id' | 'createdAt' | 'updatedAt'> & { version: number }) =>
    apiClient.put(`/api/v1/snippets/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/api/v1/snippets/${id}`),
}

// ─── Webhook Subscriptions ────────────────────────────────────────────────────

export const webhookSubscriptionApi = {
  getAll: (params: WebhookSubscriptionSearchParams = {}) => {
    const query = new URLSearchParams()
    if (params.filter) query.set('filter', params.filter)
    if (params.eventType) query.set('eventType', params.eventType)
    if (params.isEnabled !== undefined) query.set('isEnabled', String(params.isEnabled))
    if (params.activeOnly !== undefined) query.set('activeOnly', String(params.activeOnly))
    if (params.pageNumber) query.set('pageNumber', String(params.pageNumber))
    if (params.pageSize) query.set('pageSize', String(params.pageSize))
    const qs = query.toString()
    return apiClient
      .get<WebhookSubscriptionPagedResponse>(`/api/v1/webhook-subscriptions${qs ? `?${qs}` : ''}`)
      .then(r => r.data)
  },

  getById: (id: string) =>
    apiClient.get<WebhookSubscription>(`/api/v1/webhook-subscriptions/${id}`).then(r => r.data),

  create: (data: Omit<WebhookSubscription, 'id'>) =>
    apiClient.post<WebhookSubscription>('/api/v1/webhook-subscriptions', data).then(r => r.data),

  update: (id: string, data: Omit<WebhookSubscription, 'id'>) =>
    apiClient.put(`/api/v1/webhook-subscriptions/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/api/v1/webhook-subscriptions/${id}`),
}

// ─── Webhook delivery attempts ────────────────────────────────────────────────

export const webhookDeliveryAttemptApi = {
  getAll: (params: WebhookDeliveryAttemptSearchParams = {}) => {
    const query = new URLSearchParams()
    if (params.filter) query.set('filter', params.filter)
    if (params.webhookSubscriptionId) query.set('webhookSubscriptionId', params.webhookSubscriptionId)
    if (params.success !== undefined) query.set('success', String(params.success))
    if (params.pageNumber) query.set('pageNumber', String(params.pageNumber))
    if (params.pageSize) query.set('pageSize', String(params.pageSize))
    const qs = query.toString()
    return apiClient
      .get<WebhookDeliveryAttemptPagedResponse>(`/api/v1/webhook-delivery-attempts${qs ? `?${qs}` : ''}`)
      .then(r => r.data)
  },

  retryFailed: (attemptIds: string[]) =>
    apiClient
      .post<WebhookDeliveryRetryBatchResult>('/api/v1/webhook-delivery-attempts/retry', { attemptIds })
      .then(r => r.data),
}